/**
 * Session cache used to retain the admin email while the authenticated request
 * resolves. The server remains authoritative for session validity.
 * A real 401 clears this cache; local expiry mirrors the cookie's Max-Age.
 */
const SESSION_KEY = "redempta-session"
const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000

interface CachedSession {
  email: string
  expiresAt: number
}

export function cacheSession(email: string): void {
  try {
    localStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ email, expiresAt: Date.now() + SESSION_TTL_MS } satisfies CachedSession),
    )
  } catch {
    // storage unavailable (private mode etc.) — offline auth simply won't work
  }
}

/** The cached session, or null when absent or expired (expired clears itself). */
export function getCachedSession(): { email: string } | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedSession
    if (typeof parsed.email !== "string" || typeof parsed.expiresAt !== "number") {
      localStorage.removeItem(SESSION_KEY)
      return null
    }
    if (parsed.expiresAt <= Date.now()) {
      localStorage.removeItem(SESSION_KEY)
      return null
    }
    return { email: parsed.email }
  } catch {
    return null
  }
}

export function clearCachedSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY)
  } catch {
    // ignore
  }
}
